import { Stack } from '@/components/layout';
import { useSelectContext } from '@/components/ui/select/select-context';
import { SelectItem } from '@/components/ui/select/select-item';
import { useStyles } from '@/components/ui/select/styles';
import type { SelectContentProps } from '@/components/ui/select/types';
import { useBottomInsetSmart, useScaling } from '@/hooks';
import { Device } from '@/theme/scaling';
import { FC, memo, useMemo } from 'react';
import { FlatList, Modal, Platform, Pressable, ScrollView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export const SelectContent: FC<SelectContentProps> = memo(
  ({ style, maxHeight, itemTextVariant, children, statusBarTranslucent = false }) => {
    const {
      open,
      setOpen,
      options,
      selectedValue: _selectedValue,
      disabled,
      triggerLayout,
    } = useSelectContext();
    // get status bar height
    const statusBarHeight = Device.statusBarHeight;
    const { scale, deviceWidth, deviceHeight } = useScaling();
    const styles = useStyles();
    const bottomInset = useBottomInsetSmart();
    const { top } = useSafeAreaInsets();

    const selectedValue = useMemo(() => {
      const opt = options.find(o => o.value === _selectedValue);
      return opt?.value ?? null;
    }, [_selectedValue, options]);

    if (!open) return null;

    const resolvedMaxHeight = maxHeight ?? scale(320);
    const modalHorizontalPadding = scale(12);
    const triggerGap = 0;
    const edgePadding = scale(8);

    const fallbackWidth = deviceWidth - modalHorizontalPadding * 2;
    const contentWidth = triggerLayout?.width ?? fallbackWidth;
    const triggerX = triggerLayout?.x ?? modalHorizontalPadding;
    const triggerY = triggerLayout?.y ?? scale(120);
    const triggerHeight = triggerLayout?.height ?? scale(48);

    const clampedLeft = Math.max(
      edgePadding,
      Math.min(triggerX, deviceWidth - contentWidth - edgePadding),
    );

    const belowTop = triggerY + triggerHeight + triggerGap;
    const tabletAndroidExtraSpace =
      Platform.select({
        ios: 0,
        android: !statusBarTranslucent ? bottomInset + statusBarHeight : 0,
      }) ?? 0;
    const spaceBelow = Math.max(0, deviceHeight - edgePadding - belowTop - bottomInset - scale(16));
    const spaceAbove = Math.max(0, triggerY - triggerGap - edgePadding - top);
    const placeBelow =
      spaceBelow >= Math.min(scale(240), resolvedMaxHeight) || spaceBelow >= spaceAbove;
    const availableHeight = Math.max(
      scale(220),
      placeBelow
        ? spaceBelow - (Platform.select({ ios: 0, android: triggerHeight }) ?? 0)
        : spaceAbove,
    );
    const displayedMaxHeight = Math.min(resolvedMaxHeight, availableHeight);
    const verticalAnchorStyle = placeBelow
      ? { top: belowTop }
      : {
          // Anchor by bottom when placing above trigger, so short content still hugs trigger.
          bottom: deviceHeight - triggerY - tabletAndroidExtraSpace,
        };

    return (
      <Modal
        transparent
        visible={open}
        animationType="fade"
        supportedOrientations={['portrait', 'landscape']}
        onRequestClose={() => setOpen(false)}
        statusBarTranslucent={statusBarTranslucent}>
        <Stack style={styles.modalRoot}>
          <Pressable style={styles.modalBackdrop} onPress={() => setOpen(false)} />

          <Stack
            style={[
              styles.modalContentContainer,
              verticalAnchorStyle,
              { left: clampedLeft, width: contentWidth, maxHeight: displayedMaxHeight },
            ]}>
            <Stack style={[styles.contentWrapper, { maxHeight: displayedMaxHeight }, style]}>
              {children ? (
                <ScrollView
                  keyboardShouldPersistTaps="handled"
                  contentContainerStyle={styles.contentScroll}>
                  {children}
                </ScrollView>
              ) : (
                <FlatList
                  data={options}
                  keyExtractor={item => item.value}
                  keyboardShouldPersistTaps="handled"
                  renderItem={({ item }) => (
                    <SelectItem
                      value={item.value}
                      label={item.label}
                      style={undefined}
                      disabled={disabled}
                      isSelected={(item.value ?? '') === (selectedValue ?? '')}
                      itemTextVariant={itemTextVariant}
                    />
                  )}
                  scrollEnabled={options.length > 0}
                />
              )}
            </Stack>
          </Stack>
        </Stack>
      </Modal>
    );
  },
);

SelectContent.displayName = 'Select.Content';
